import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, getDocs } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useUserSettings } from './useUserSettings';
import { logError, logInfo } from '../utils/logger';
import { calculateRouteDistance } from '../utils/routeHelpers';

// Cache for POIs and routes subscriptions
const subscriptionCache = new Map();

const useSurveys = (showAll = false) => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { settings } = useUserSettings();
  const [localActiveSurveyId, setLocalActiveSurveyId] = useState(() => localStorage.getItem('activeSurveyId'));
  const [localIsRecording, setLocalIsRecording] = useState(() => localStorage.getItem('isRecording') === 'true');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Add refetch function to reload surveys
  const refetch = useCallback(async () => {
    setRefreshTrigger(prev => prev + 1);
    logInfo('Survey list refresh triggered');
  }, []);

  useEffect(() => {
    // Custom event handler for local updates
    const handleLocalStorageUpdate = (event) => {
      const { key, newValue } = event.detail;
      if (key === 'activeSurveyId') {
        setLocalActiveSurveyId(newValue);
        setRefreshTrigger(prev => prev + 1);
      } else if (key === 'isRecording') {
        setLocalIsRecording(newValue === 'true');
        setRefreshTrigger(prev => prev + 1);
      }
    };

    // Listen for both storage events (other tabs) and custom events (current tab)
    const handleStorageChange = (e) => {
      if (e.key === 'activeSurveyId') {
        setLocalActiveSurveyId(e.newValue);
        setRefreshTrigger(prev => prev + 1);
      } else if (e.key === 'isRecording') {
        setLocalIsRecording(e.newValue === 'true');
        setRefreshTrigger(prev => prev + 1);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageUpdate', handleLocalStorageUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdate', handleLocalStorageUpdate);
    };
  }, []);

  useEffect(() => {
    if (!auth.currentUser) {
      setSurveys([]);
      setLoading(false);
      return;
    }

    // Custom event handler for local updates
    const handleLocalStorageUpdate = (event) => {
      const { key, newValue } = event.detail;
      if (key === 'activeSurveyId') {
        setLocalActiveSurveyId(newValue);
        setRefreshTrigger(prev => prev + 1);
      } else if (key === 'isRecording') {
        setLocalIsRecording(newValue === 'true');
        setRefreshTrigger(prev => prev + 1);
      }
    };

    // Listen for both storage events (other tabs) and custom events (current tab)
    const handleStorageChange = (e) => {
      if (e.key === 'activeSurveyId') {
        setLocalActiveSurveyId(e.newValue);
        setRefreshTrigger(prev => prev + 1);
      } else if (e.key === 'isRecording') {
        setLocalIsRecording(e.newValue === 'true');
        setRefreshTrigger(prev => prev + 1);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageUpdate', handleLocalStorageUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdate', handleLocalStorageUpdate);
    };
  }, []);

  useEffect(() => {
    if (!auth.currentUser) return;

    const recentLimit = settings?.preferences?.recentSurveysLimit || 5;
    
    // Create queries for both owned and shared surveys
    const surveysRef = collection(db, 'surveys');
    
    // Query for owned surveys
    const ownedQuery = query(
      surveysRef,
     where('ownerId', '==', auth.currentUser.uid),
      orderBy('updatedAt', 'desc')
    );

    // Query for shared surveys  
    const sharedQuery = query(
      surveysRef,
      where('sharedWith', 'array-contains', auth.currentUser.uid),
      orderBy('updatedAt', 'desc')
    );

    // Subscribe to both queries
    const unsubscribeOwned = onSnapshot(ownedQuery, async (ownedSnapshot) => {
      const unsubscribeShared = onSnapshot(sharedQuery, async (sharedSnapshot) => {
        try {
          // Combine owned and shared surveys
          const ownedSurveys = ownedSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            isActive: doc.id === localActiveSurveyId,
            isRecording: doc.id === localActiveSurveyId && localIsRecording
          }));
          
          const sharedSurveys = sharedSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            sharedWith: doc.data().sharedWith || [],
            isActive: doc.id === localActiveSurveyId,
            isRecording: doc.id === localActiveSurveyId && localIsRecording,
            isShared: true
          }));
          
          const allSurveys = [...ownedSurveys, ...sharedSurveys];

          // Update surveys immediately with existing stats
          setSurveys(allSurveys.map(survey => ({
            ...survey,
            stats: survey.stats || { poisCount: 0, routesCount: 0, totalDistance: 0 }
          })));

          // Set up or update subscriptions for each survey
          allSurveys.forEach(survey => {
            if (!subscriptionCache.has(survey.id)) {
              // Subscribe to POIs
              const poisRef = collection(db, 'pois');
              const poisQuery = query(poisRef, where('surveyId', '==', survey.id));
              const unsubscribePOIs = onSnapshot(poisQuery, poisSnapshot => {
                const poisCount = poisSnapshot.size;
                setSurveys(prev => prev.map(s => 
                  s.id === survey.id 
                    ? { ...s, stats: { ...s.stats, poisCount } }
                    : s
                ));
              });

              // Subscribe to routes
              const routesRef = collection(db, 'routes');
              const routesQuery = query(routesRef, where('surveyId', '==', survey.id));
              const unsubscribeRoutes = onSnapshot(routesQuery, routesSnapshot => {
                const routes = routesSnapshot.docs.map(doc => doc.data());
                const totalDistance = routes.reduce((total, route) => {
                  return total + (route.trackPoints?.length > 1 ? 
                    calculateRouteDistance(route.trackPoints) : 0);
                }, 0);
                setSurveys(prev => prev.map(s => 
                  s.id === survey.id 
                    ? { 
                        ...s, 
                        stats: { 
                          ...s.stats, 
                          routesCount: routes.length,
                          totalDistance
                        }
                      }
                    : s
                ));
              });

              // Store unsubscribe functions
              subscriptionCache.set(survey.id, {
                pois: unsubscribePOIs,
                routes: unsubscribeRoutes
              });
            }
          });

          // Clean up unused subscriptions
          const currentIds = new Set(allSurveys.map(s => s.id));
          for (const [id, unsubs] of subscriptionCache.entries()) {
            if (!currentIds.has(id)) {
              unsubs.pois();
              unsubs.routes();
              subscriptionCache.delete(id);
            }
          }

          setLoading(false);
          setError(null);
        } catch (error) {
          logError('Error processing surveys:', error);
          setError('Failed to load surveys');
          setLoading(false);
        }
      });
    }, error => {
      logError('Survey subscription error:', error);
      setError('Failed to load surveys');
      setLoading(false);
    });

    // Cleanup function
    return () => {
      unsubscribeOwned();
      // Clean up all cached subscriptions
      for (const unsubs of subscriptionCache.values()) {
        unsubs.pois();
        unsubs.routes();
      }
      subscriptionCache.clear();
    };
  }, [settings?.preferences?.recentSurveysLimit, showAll, auth.currentUser, localActiveSurveyId, localIsRecording, refreshTrigger]);

  return { 
    surveys, 
    loading, 
    error,
    refetch
  };
};

export default useSurveys;

export { useSurveys }